import './assets/tailwind.css'
import './assets/main.css'
import 'vue-toastification/dist/index.css'
import './assets/nprogress.css'

import { createSSRApp } from 'vue'
import { createPinia } from 'pinia'
import { VueQueryPlugin, type VueQueryPluginOptions } from '@tanstack/vue-query'
import Toast from 'vue-toastification'
import moment from 'moment'
import { autoAnimatePlugin } from '@formkit/auto-animate/vue'
import App from './App.vue'
import { i18n } from './i18n'
import { createAppRouter } from './router'
import { setActiveRouter } from './router/runtime'
import { recordError, recording, sendSessionReplay } from './services/tracking-sessions'
import { useGlobalStore } from './stores/global'
import { appsHealthContextKey, type AppsHealthContext } from './services/apps-health-context'

moment.locale('ar-kw')
moment.updateLocale('ar-kw', {
  week: {
    dow: 0,
    doy: 6
  }
})

const vueQueryPluginOptions: VueQueryPluginOptions = {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        refetchOnWindowFocus: false
      }
    }
  }
}

export type AppInitialContext = {
  appsHealth?: AppsHealthContext
}

export const createVueApp = (initialContext: AppInitialContext = {}) => {
  const app = createSSRApp(App)
  const pinia = createPinia()
  const router = createAppRouter()

  setActiveRouter(router)

  app.use(pinia)
  app.config.globalProperties.$globalStore = useGlobalStore(pinia)

  if (initialContext.appsHealth) {
    app.provide(appsHealthContextKey, initialContext.appsHealth)
  }

  app
    .use(VueQueryPlugin, vueQueryPluginOptions)
    .use(i18n)
    .use(router)
    .use(Toast, {
      rtl: true
    })
    .use(autoAnimatePlugin)

  if (!import.meta.env.SSR) {
    app.config.errorHandler = async (err) => {
      if (!router.currentRoute.value.fullPath.startsWith('admin') && recording) {
        try {
          recordError(err)
          await sendSessionReplay()
        } catch (error) {}
      }
    }
  }

  return { app, pinia, router }
}
